import React, { useEffect, useState, createContext, useContext, PropsWithChildren, useMemo, useRef } from 'react';
import { AuthTokens, USER_AUTH_EVENT, USER_AUTH_KEY, USER_AUTH_TOKENS_EVENT, USER_AUTH_TOKENS_KEY, UserLogin } from './AuthClasses';
import { APIResponse, LoginSource } from '@arema/components/Classes';
import API, { CAPTCHA_ACTIVE, DEBUG, RECAPTCHA_KEY } from './AuthAPI';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

const EXPIRED_CODES = [
	'P-TKNV', 'P-RFTKNV', 'P-TKNE',
]

var AuthContext = createContext<{
	user: UserLogin,
	user_saved: boolean,
	loginSaved: ()=>Promise<APIResponse<UserLogin>>,
	clearSaved: ()=>void,
	loggedIn: boolean,
	getContinueUrl: (t?: AuthTokens, u?: string)=>string,
	getCaptcha: ()=>Promise<string>
	is_pdv: boolean,
	params: {
		source: LoginSource,
		pdv_token: string,
		recover_token: string,
		confirm_token: string,
		continue_url: string,
	}
}>(null);
var useAuth = () => useContext(AuthContext);
var AuthProvider = (props: PropsWithChildren)=>{
	var [user, setUser] = useState<UserLogin>(null);
	var [tokens, setTokens] = useState<AuthTokens>(null);
	var [query, setQuery] = useSearchParams();
	var recaptchaRef = useRef<ReCAPTCHA>(null);

	var { login_source, pdv_token, recover_token, confirm_token, is_pdv, continue_url, redirect_url, valid_source, valid_continue } = useMemo(()=>{
		var pdv_token = query.get('pdv') || null;
		var continue_url = decodeURIComponent(query.get('continue') || '');
		var login_source : LoginSource = (parseInt(query.get('s')) || null);

		return {
			login_source,
			pdv_token,
			recover_token: query.get('ft') || null,
			confirm_token: query.get('ct') || null,
			is_pdv: !!pdv_token && pdv_token.length>10,
			continue_url: continue_url && continue_url.length<10 ? null : continue_url,
			redirect_url: query.get('redirect'),
			valid_source: Object.values(LoginSource).indexOf(login_source)!=-1,
			valid_continue: !!continue_url && continue_url.length>=10,
		}
	}, [query]);



	useEffect(()=>{
		if(user===null) onLogin();
		window.addEventListener(USER_AUTH_EVENT, onLogin);
		window.addEventListener(USER_AUTH_TOKENS_EVENT, onTokens);
		return ()=>{
			window.removeEventListener(USER_AUTH_EVENT, onLogin);
			window.removeEventListener(USER_AUTH_TOKENS_EVENT, onTokens);
		}
	}, [user]);

	var onTokens = ()=>{
		var tokens = window.localStorage.getItem(USER_AUTH_TOKENS_KEY);
		if(DEBUG) console.log("[EVENT]: onTokens", tokens);
		try{
			var tokens_data = JSON.parse(tokens) as AuthTokens;
			var now = moment().unix();
			if(tokens_data?.expires<=(now+30)){
				window.localStorage.removeItem(USER_AUTH_TOKENS_KEY);
			}
			setTokens(tokens_data);
		}catch(e){
			console.log(e);
		}
	}

	var onLogin = ()=>{
		var login = window.localStorage.getItem(USER_AUTH_KEY);
		if(DEBUG) console.log("[EVENT]: onLogin", login);
		try{
			var login_data = JSON.parse(login) as UserLogin;
			var now = moment().unix();
			setUser(login_data);
			if(!login_data?.tokens || login_data?.tokens.refresh_expires<=(now+30)){
				window.localStorage.removeItem(USER_AUTH_KEY);
			}
		}catch(e){
			console.log(e);
		}
	}

	var loginSaved = () : Promise<APIResponse<UserLogin>>=>{
		if(!user || !user.tokens) return Promise.resolve({
			error: true,
			code: 'LCL-EXTSV-1',
			message: 'No existe una autenticación válida (LCL-EXTSV-1)'
		})
		return new Promise((resolve, reject)=>{
			API.loginRefresh(user.tokens.refresh, login_source, (!continue_url || continue_url.length<5)).then(res=>{
				if(res.error && EXPIRED_CODES.indexOf(res.code)!==-1){
					clearSaved();
				}
				return resolve(res);
			}).catch(reject);
		})
	}

	var clearSaved = ()=>{
		window.localStorage.removeItem(USER_AUTH_KEY);
		setUser(null);
	}

	var getContinueUrl = (auth_tokens?: AuthTokens, conurl: string=null)=>{
		var tk = auth_tokens || tokens;
		var url = conurl || continue_url;

		if(!url || !tk) return null;
		var auth_url = url.replace(/\/$/gi, '');
		var params : any = {
			at: tk.access,
			e: tk.expires,
		}
		
		var refresh : AuthTokens = auth_tokens?.refresh ? auth_tokens : (user?.tokens.refresh ? user.tokens : null);

		if(refresh && [LoginSource.PDV, LoginSource.SCANNER].indexOf(login_source)!=-1){
			params.rt = refresh.refresh;
			params.re = refresh.refresh_expires;
		}
		
		if(redirect_url && redirect_url.length>=10){
			params.r = redirect_url;
		}
		return `${auth_url}?${Object.keys(params).map(a=>`${a}=${params[a]}`).join('&')}`
	}

	var getCaptcha = async ()=>{
		if(!CAPTCHA_ACTIVE) return 'INACTIVE_CAPTCHA';
		if(!recaptchaRef.current) return null;
		recaptchaRef.current?.reset();
		return recaptchaRef.current?.executeAsync()
	}
	
	var now = moment().unix();
	
	return <AuthContext.Provider value={{
		user,
		user_saved: !is_pdv && user && user.tokens?.refresh && user.tokens.refresh_expires>(now+30),
		loginSaved,
		clearSaved,
		getContinueUrl,
		getCaptcha,
		loggedIn: !!tokens,
		is_pdv,
		params: {
			source: valid_source ? login_source : null,
			confirm_token,
			pdv_token,
			recover_token,
			continue_url: valid_continue ? continue_url : null,
		}
	}}>
		{props.children}
		{CAPTCHA_ACTIVE && (
			<ReCAPTCHA 
				hl='es-419'
				size='invisible'
				sitekey={RECAPTCHA_KEY}
				ref={recaptchaRef}
			/>
		)}
	</AuthContext.Provider>
}

export {
	useAuth,
	AuthProvider,
}